/* eslint-disable camelcase */
import { useCallback } from 'react';
import {
  get, put, post, destroy,
} from './index';
import cleanUpObject from '../cleanUpObject';

/**
 * Data for create note with its properties
 * @typedef {Object} CreateNoteData
 * @property {number} site_id - The ID of the site.
 * @property {number} user_id - The ID of the user.
 * @property {string} title - The title of the note.
 * @property {string} content - The content of the note.
 * @property {number} status - The status of the note.
 * @property {string} priority - The priority of the note.
 * @property {string} timestamp - The timestamp of the note.
 */

/**
   * Send request to create a new note
   * @param {number} orgId organization id
   * @param {CreateNoteData} data - Note data
   * @returns Promise with http response
   * the props order is important because we are sending create or edit note request to the server from the same function in NoteViewAddEditModal (with ot without noteId)
   */
export const createNote = (orgId, data) => {
  return post(`/api/v1/notes?organization_id=${orgId}`, data);
};

/**
   * Send request to get a notes
   * @param {number} orgId organization id
   * @param {object} params api params for searching by name, start and end date
   * @returns Promise with http response regarding getting notes
   */
export const getNotes = (orgId, params = {}) => {
  const {
    noteName = '',
    from = '',
    to = '',
    sort = 'timestamp',
    ordering = 'desc',
  } = params;
  const queryParamString = new URLSearchParams({
    name: noteName,
    start_date: from,
    end_date: to,
    sort,
    ordering,
    organization_id: orgId,
  }).toString();

  return get(`/api/v1/notes?${queryParamString}`);
};

export const getNotesPage = (organizationId, params) => {
  let queryParamString = {
    organization_id: organizationId,
    site_name: params.site_name || '',
    note_content_title: params.note_content_title || '',
    status: params.status || '',
    priority: params.priority || '',
    timestamp: params.timestamp || '',
    sort: params.sortBy || 'timestamp',
    ordering: params.direction || 'asc',
    per_page: params.perPage || 9,
    page: params.page || 1,
  };
  cleanUpObject(queryParamString);
  queryParamString = new URLSearchParams(queryParamString).toString();
  return get(`/api/v1/notes?${queryParamString}`);
};

/**
 * Get a note by its id and organization id
 * @param {number} noteId note id
 * @param {number} orgId organization id
 * @returns {Promise} Promise with http response
 */

export const getNote = (noteId, orgId) => get(`/api/v1/notes/${noteId}?organization_id=${orgId}`);

/**
 * Data for update with its properties
 * @typedef {Object} UpdateNoteData
 * @property {string} title - The title of the note.
 * @property {string} content - The content of the note.
 * @property {number} status - The status of the note.
 * @property {string} priority - The priority of the note.
 * @property {string} timestamp - The timestamp of the note.
 */

/**
 * Update a note by its id and organization id
 * @param {number} noteId note id
 * @param {number} orgId organization id
 * @param {UpdateNoteData} data note data we want to update
 * @returns {Promise} Promise with http response
 * the props order is important because we are sending create or edit note request to the server from the same function in NoteViewAddEditModal (with ot without noteId)
 */
export const updateNote = (orgId, data, noteId) => put(`/api/v1/notes/${noteId}?organization_id=${orgId}`, data);

/**
 * Delete a note by its id and organization id
 * @param {number} noteId note id
 * @param {number} orgId organization id
 * @returns {Promise} Promise with http response
 */

export const deleteNote = (noteId, orgId) => destroy(`/api/v1/notes/${noteId}?organization_id=${orgId}`);

/**
   * Fetch a note by id using memoization
   * @param {number} noteId note id
   * @param {number} orgId organization id
   * @returns Promise with http response
   */
export const useFetchNote = (noteId, orgId, setNote, showToast) => useCallback(() => {
  getNote(noteId, orgId)
    .then((response) => {
      return response.json();
    })
    .then((data) => {
      if (!data.note) {
        throw new Error(data.error);
      }
      setNote(data.note); // rewrite note if note updated
    })
    .catch((error) => {
      showToast({
        message: `Failed to fetch note: ${error.message}.`,
        type: 'error',
      });
    });
}, [noteId, orgId, setNote, showToast]);
